import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import HistoricoConsultasChart from "../../graphs/HistoricoConsultasChart";
import ReactApexChart from "react-apexcharts";
import ScoreRating from "./ScoreRating";
import { Badge, Modal, Table } from "flowbite-react";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Socios from "../../common/query/Socios";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import DecisionBanner from "../DecisionBanner";
import StreetView from "../../common/query/StreetView";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import moment from "moment";
import FalenciaRecuperacaoJudicial from "../../common/query/FalenciaRecuperacaoJudicial";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { TfiBarChart } from "react-icons/tfi";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import { BsBank } from "react-icons/bs";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
  GrMoney,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { BiLineChartDown } from "react-icons/bi";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true,
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

const firstNonEmptySeries = (src, keys) => {
  for (const k of keys) {
    const arr = src?.[k];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const SerasaAvancadoPJQuery = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);
  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao,
  );
  const fundacaoMoment = data?.dadosCadastrais?.dataFundacao
    ? moment(data?.dadosCadastrais?.dataFundacao, [
        "DD/MM/YYYY",
        "YYYY-MM-DD",
        "YYYYMMDD",
      ])
    : null;
  const fundacaoLabel = fundacaoMoment?.isValid()
    ? fundacaoMoment.format("DD/MM/YYYY")
    : null;

  // Fallbacks para novos campos vindos do formatador
  const sociosEmpresa = data?.socios;
  const administradoresEmpresa = data?.administradores;

  const participacoesHierarquico =
    data?.participacoesSociosAdmOutrasEmpresas || [];
  const participacoesEmpresaConsultada =
    dataFull?.participacoesEmpresaConsultada ||
    data?.participacaoEmEmpresas ||
    [];

  const faturamentoPresumido = dataFull?.faturamentoPresumido;

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade,
      ),
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  const compromissosSeries = data?.compromissos?.series ?? [];
  const creditosSeries = data?.creditoObtido?.series ?? [];
  const periodosSeries = [
    ...new Set(
      [
        ...compromissosSeries.map((pt) => pt.mes).filter(Boolean),
        ...creditosSeries.map((pt) => pt.mes).filter(Boolean),
      ].filter(Boolean),
    ),
  ];
  const getCompromissoSerie = (mes) =>
    compromissosSeries.find((pt) => pt.mes === mes);
  const getCreditoSerie = (mes) => creditosSeries.find((pt) => pt.mes === mes);
  const formatCompromissoValor = (valor) => {
    const parsed = parseFloat(valor);
    if (isNaN(parsed)) {
      return "–";
    }
    if (parsed === 0) {
      return "< 10";
    }
    return parsed * 100;
  };
  const formatCreditoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };

  const statusConsumidorContainsNao = data?.statusConsumidor
    ? data?.statusConsumidor.toUpperCase().includes("NÃO") ||
      data?.statusConsumidor.toUpperCase().includes("NAO") ||
      data?.statusConsumidor.toUpperCase().includes("CANCELADA") ||
      data?.statusConsumidor.toUpperCase().includes("SEM")
    : false;

  const rawDemaisInformacoes =
    data?.demaisInformacoes ?? data?.demais_informacoes ?? {};
  const tempoRelacionamentoRaw =
    rawDemaisInformacoes?.tempoRelacionamentoFornecedores ??
    rawDemaisInformacoes?.tempo_relacionamento_fornecedores
      ?.historico_periodo ??
    rawDemaisInformacoes?.tempo_relacionamento_fornecedores ??
    [];
  const tempoRelacionamento = Array.isArray(tempoRelacionamentoRaw)
    ? tempoRelacionamentoRaw
    : [];
  const tempoValores = tempoRelacionamento.map((item) => {
    const valor = Number(item?.valor ?? 0);
    return Number.isFinite(valor) ? valor : 0;
  });
  const maxTempoValor = tempoValores.length > 0 ? Math.max(...tempoValores) : 0;
  const eventosImportantesRaw =
    rawDemaisInformacoes?.acontecimentosImportantes ??
    rawDemaisInformacoes?.acontecimentos_importantes
      ?.historico_periodo_pontuacao ??
    rawDemaisInformacoes?.acontecimentos_importantes ??
    [];
  const eventosImportantes = Array.isArray(eventosImportantesRaw)
    ? eventosImportantesRaw
    : [];
  const hasDemaisInformacoes =
    eventosImportantes.length > 0 || tempoRelacionamento.length > 0;
  const getTempoBarWidth = (valor) => {
    const safeValor = Number.isFinite(Number(valor)) ? Number(valor) : 0;
    if (maxTempoValor <= 0) {
      return 0;
    }
    const percent = (safeValor / maxTempoValor) * 100;
    return Math.min(Math.max(percent, 4), 100);
  };
  const getEventoValor = (evento) => {
    if (evento?.valor != null && evento?.valor !== "") {
      return evento.valor;
    }
    if (evento?.pontuacao != null) {
      return formatToReal(evento.pontuacao);
    }
    return "—";
  };

  return (
    <div>
      <TitleStripe>
        <div className="flex items-center gap-2">
          <LuBuilding2 className="text-blue-700" />{" "}
          <span>Dados Cadastrais / Base II</span>
        </div>
      </TitleStripe>
      <div className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Razão Social
            </p>
            <p className="text-xl font-semibold text-gray-900">
              {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
            </p>
            <p className="text-[11px] text-gray-500">
              {data?.dadosCadastrais?.nomeFantasia || "Sem nome fantasia"}
            </p>
          </div>
          <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-500">
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
            {data?.dadosCadastrais?.situacao || "Situação desconhecida"}
          </span>
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
            Fundação: {fundacaoLabel || "—"}
          </span>
          {companyAgeLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5">
              {companyAgeLabel}
            </span>
          )}
        </div>

        <div className="mt-3 grid gap-2 grid-cols-3">
          {[
            {
              label: "CNPJ",
              value: data?.dadosCadastrais?.cnpj
                ? formatDocument(data?.dadosCadastrais?.cnpj)
                : "Não disponível",
              hint: "Documento",
            },
            {
              label: "Inscrição Estadual",
              value:
                data?.dadosCadastrais?.inscricaoEstadual || "Não informado",
              hint: "Inscrição",
            },
            {
              label: "CNAE principal",
              value: data?.dadosCadastrais?.ramoAtividade || "Não informado",
              hint: "Atividade",
            },
            {
              label: "Segmento",
              value: data?.dadosCadastrais?.segmento || "Não informado",
              hint: "Setor",
            },
            {
              label: "Natureza jurídica",
              value: data?.dadosCadastrais?.naturezaJuridica || "Não informado",
              hint: "Perfil",
            },
            {
              label: "Capital social",
              value: (() => {
                const capitalValue =
                  data?.dadosCadastrais?.capitalSocial ??
                  data?.dadosCadastrais?.capitalInicial;
                return capitalValue != null && capitalValue !== ""
                  ? formatToReal(capitalValue)
                  : "—";
              })(),
              hint: "Recursos iniciais",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-100 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <TitleStripe className="mt-6 mb-3">
        <div className="flex items-center gap-2">
          <LuMapPin className="text-blue-700" />{" "}
          <span>Localização / Base II</span>
        </div>
      </TitleStripe>

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            Endereço
          </p>
          <div className="flex items-center gap-2">
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.endereco || "—"}
            </p>
            {fullAddress && (
              <div
                className=" flex items-center gap-1 text-[11px] text-blue-600 cursor-pointer"
                onClick={() => setOpenMap(true)}
              >
                <span>- Ver fotos do local</span>
                <HiOutlineEye className="text-base" />
              </div>
            )}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            Bairro · CEP
          </p>
          <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
            <span>{data?.dadosCadastrais?.bairro || "—"}</span>
            <span>·</span>
            <span>{maskCEP(data?.dadosCadastrais?.cep) || "--"}</span>
          </p>
        </div>
      </div>

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            Cidade
          </p>
          <p className="text-[12px] font-semibold text-gray-900">
            {data?.dadosCadastrais?.cidade || "—"}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
            UF
          </p>
          <p className="text-[12px] font-semibold text-gray-900">
            {data?.dadosCadastrais?.uf || "--"}
          </p>
        </div>
      </div>
      {/* Avaliação preliminar */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FaRegChartBar className="text-blue-700" />{" "}
          <span>Avaliação preliminar de crédito / Base II</span>
        </div>
      </TitleStripe>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}

      <div>
        <TitleStripe>
          <div className="flex items-center gap-2">
            <BsBank className="text-blue-700" />{" "}
            <span>Limite de crédito / Base II</span>
          </div>
        </TitleStripe>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700">
                <BsBank className="h-4 w-4" />
              </span>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                  Estimativa sugerida
                </div>
                <div className="text-xs text-slate-400">Limite recomendado</div>
              </div>
            </div>
            <span className="hidden rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-700 sm:inline-flex">
              Crédito
            </span>
          </div>

          <div className="px-4 py-4">
            <div className="text-xl font-extrabold text-slate-900">
              {data?.limiteCredito?.score
                ? formatToReal(data.limiteCredito.score)
                : "Indisponível"}
            </div>

            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
              Limite de crédito estimado a partir de informações comportamentais
              e cadastrais da empresa.
            </div>
          </div>
        </div>
      </div>

      {/* Quadro societário */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <FiUsers className="text-blue-700" />
            <span>Quadro Societário / Base II</span>
          </div>
        </TitleStripe>
        {sociosEmpresa?.length > 0 ? (
          <Socios socios={sociosEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>
      {/* Administradores */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <FiUsers className="text-blue-700" />
            <span>Administradores / Base II</span>
          </div>
        </TitleStripe>
        {administradoresEmpresa?.length > 0 ? (
          <Socios isAdm={true} socios={administradoresEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Faturamento Presumido */}
      {faturamentoPresumido &&
        (faturamentoPresumido?.valor != null ||
          faturamentoPresumido?.series?.length > 0) && (
          <div>
            <TitleStripe className="mb-4">
              <div className="flex items-center gap-2">
                <GrMoney className="text-blue-700" />{" "}
                <span>Faturamento Presumido / Base II</span>
              </div>
            </TitleStripe>

            <div className="mt-2 font-semibold">
              Estimado:{" "}
              <Badge className="w-fit inline" color="gray">
                {faturamentoPresumido?.valor != null
                  ? formatToReal(faturamentoPresumido.valor)
                  : "–"}
              </Badge>
            </div>

            {faturamentoPresumido?.series?.length > 0 && (
              <div className="mt-2 w-full rounded-lg border border-gray-300 overflow-auto">
                <Table>
                  <Table.Head>
                    {faturamentoPresumido.series.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {faturamentoPresumido.series.map((pt, idx) => (
                        <Table.Cell
                          key={idx}
                          className="text-center text-black text-xs px-1"
                        >
                          {pt?.valor != null ? formatToReal(pt.valor) : "–"}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}

            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Estimativa baseada em sinais comportamentais e declarações
              públicas disponíveis para o CNPJ. Use com cautela.
            </div>
          </div>
        )}
      {/* Negativas, protestos, ações, cheques */}
      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Pendências Financeiras / Base II</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <PendenciasFinanceiras
            quantidadeTotal={
              data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
            }
            valorTotal={data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal}
            pendenciasFinanceiras={data?.pendenciasFinanceiras}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentExcel className="text-blue-700" />
            <span>Títulos protestados / Base II</span>
          </div>
        </TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div id="falencias-rec-judiciais-base-2">
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <BiLineChartDown className="text-blue-700" />
            <span>Falências/Recuperações Judiciais / Base II</span>
          </div>
        </TitleStripe>
        {data?.falenciasRecJudiciais?.length > 0 ? (
          <FalenciaRecuperacaoJudicial
            falenciaRecuperacaoJudicial={data?.falenciasRecJudiciais?.map(
              (fal) => ({ ...fal, tipo_ocorrencia: fal.tipo }),
            )}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo / Base II</span>
          </div>
        </TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <ChequesSemFundos
            quantidadeTotal={
              data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal
            }
            chequesSemFundos={data?.chequesSemFundo}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* Histórico de consultas */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas / Base II</span>
          </div>
        </TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-4">
            <div className="flex-1">
              <HistoricoConsultas
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {data?.historicoConsultasResumo?.paresMesAnoHistoricoConsultas
              ?.length > 0 && (
              <div className="mt-2">
                <HistoricoConsultasChart
                  paresMesAnoHistoricoConsultas={
                    data?.historicoConsultasResumo
                      ?.paresMesAnoHistoricoConsultas || []
                  }
                  totaisHistoricoConsultas={
                    data?.historicoConsultasResumo?.totaisHistoricoConsultas ||
                    []
                  }
                  title="Gráfico de Histórico de Consultas"
                  subtitle="Evolução das consultas nos últimos meses"
                />
              </div>
            )}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SerasaAvancadoPJQuery;
